import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Package, Mail, Hash, Bell } from 'lucide-react';
import { accessories } from '../data/accessoriesPricing';

const iconMap = {
  briefkasten: Mail,
  hausnummer: Hash,
  klingel: Bell
};

const AccessoriesConfiguration = ({ selectedAccessories, onAccessoriesChange }) => {
  
  const handleToggle = (accessoryId) => {
    const newAccessories = { ...selectedAccessories };
    if (newAccessories[accessoryId]?.enabled) {
      newAccessories[accessoryId] = { enabled: false, variant: null, quantity: 1 };
    } else {
      const firstVariant = accessories[accessoryId].variants[0];
      newAccessories[accessoryId] = { 
        enabled: true, 
        variant: firstVariant.id,
        quantity: 1 
      };
    }
    onAccessoriesChange(newAccessories);
  };

  const handleVariantChange = (accessoryId, variantId) => {
    const newAccessories = { ...selectedAccessories };
    newAccessories[accessoryId] = {
      ...newAccessories[accessoryId],
      variant: variantId
    };
    onAccessoriesChange(newAccessories);
  };

  const handleQuantityChange = (accessoryId, quantity) => {
    const newAccessories = { ...selectedAccessories };
    newAccessories[accessoryId] = {
      ...newAccessories[accessoryId],
      quantity: Math.max(1, parseInt(quantity) || 1)
    };
    onAccessoriesChange(newAccessories);
  };

  const renderAccessory = (accessoryId, accessory) => {
    const Icon = iconMap[accessoryId] || Package;
    const config = selectedAccessories[accessoryId] || { enabled: false, variant: null, quantity: 1 };
    const isEnabled = config.enabled;
    const selectedVariant = accessory.variants.find(v => v.id === config.variant);

    return (
      <div key={accessoryId} className="space-y-3">
        <div 
          className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-all cursor-pointer ${
            isEnabled 
              ? 'border-emerald-500 bg-emerald-50' 
              : 'border-gray-200 hover:border-emerald-300'
          }`}
          onClick={() => handleToggle(accessoryId)}
        >
          <Checkbox
            id={accessoryId}
            checked={isEnabled}
            onCheckedChange={() => handleToggle(accessoryId)}
          />
          <Icon className={`w-5 h-5 ${isEnabled ? 'text-emerald-600' : 'text-gray-400'}`} />
          <div className="flex-1">
            <Label htmlFor={accessoryId} className="font-medium cursor-pointer">
              {accessory.name}
            </Label>
            <p className="text-sm text-gray-500">{accessory.description}</p>
          </div>
          {isEnabled && selectedVariant && (
            <span className="text-emerald-600 font-semibold">€{selectedVariant.price}</span>
          )}
        </div>

        {isEnabled && (
          <div className="ml-8 p-4 bg-gray-50 rounded-lg space-y-4 animate-slideUp">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Variante</Label>
                <Select value={config.variant || ''} onValueChange={(v) => handleVariantChange(accessoryId, v)}>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Variante wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {accessory.variants.map((variant) => (
                      <SelectItem key={variant.id} value={variant.id}>
                        {variant.label} - €{variant.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Anzahl</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={config.quantity || 1}
                  onChange={(e) => handleQuantityChange(accessoryId, e.target.value)}
                  className="bg-white"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="transition-all duration-300 hover:shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="w-5 h-5 text-emerald-600" />
          Zubehör
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600">
          Wählen Sie optionales Zubehör für Ihre Zaunanlage
        </p>

        {Object.entries(accessories).map(([id, accessory]) => 
          renderAccessory(id, accessory)
        )}

        {!Object.values(selectedAccessories).some(a => a.enabled) && (
          <div className="bg-gray-50 p-4 rounded-lg text-center">
            <p className="text-sm text-gray-500">Kein Zubehör ausgewählt</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AccessoriesConfiguration;
